import {
  CheckInput,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { Fragment, ReactChild, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { AzureFilesUploaderControlled } from '../../../../components/AzureFilesUploader/AzureFileUploaderControlled';
import { SectionTitle } from '../../../../components/text';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { formatToOnlyPositiveIntegers } from '../../../../utils/currency';
import { useProgramFormLoaderData } from '../hooks/ProgramFormLoader';
import ProgramCurriculum from './ProgramCurriculum';

export interface ProgramUnitSubject {
  subjectId: number;
  name: string;
  code: string;
}

export interface ProgramUnit {
  name: string;
  subjects: ProgramUnitSubject[];
  message?: string;
}

export interface ProgramFormValues {
  name: string;
  code: string;
  modality: SelectOptionType | null;
  school: SelectOptionType | null;
  totalHours: number;
  activityHours: { [activityTypeId: string]: number };
  description: string;
  hasCertificate: boolean;
  units: ProgramUnit[];
  curriculumImage: File | null;
}

const getDefaultValues = (): ProgramFormValues => ({
  name: '',
  code: '',
  modality: null,
  school: null,
  totalHours: 0,
  activityHours: {},
  description: '',
  hasCertificate: false,
  units: [{ name: 'Unidad 1', subjects: [] }],
  curriculumImage: null,
});

export interface ProgramFormProps {
  children: (props: { isSubmitting: boolean }) => ReactChild;
  onSubmit: (
    values: ProgramFormValues,
  ) => Promise<'CONNECTION' | 'HTTP_ERROR' | undefined>;
}

const ProgramForm = (props: ProgramFormProps) => {
  const { onSubmit: onSubmitProp, children } = props;
  const {
    schools,
    modalities,
    activityTypes,
    subjects,
  } = useProgramFormLoaderData();

  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );
  const prefix = 'programForm';

  const methods = useForm<ProgramFormValues>({
    mode: 'onChange',
    defaultValues: getDefaultValues(),
  });

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
    setValue,
    watch,
    setError,
    clearErrors,
  } = methods;

  const { msgValidations, rulesPatternDecimal } = useValidations();

  const [activityHoursValues, selectedModality, units] = watch([
    'activityHours',
    'modality',
    'units',
  ]);

  // Calculate total hours based on activity hours
  const totalHours = useMemo(() => {
    if (!activityHoursValues) return 0;
    return Object.values(activityHoursValues).reduce(
      (sum, hours) => sum + Number(hours || 0),
      0,
    );
  }, [activityHoursValues]);

  useEffect(() => {
    setValue('totalHours', totalHours);
  }, [totalHours, setValue]);

  // Filter subjects based on selected modality
  const filteredSubjects = useMemo(() => {
    if (!selectedModality) return [];

    const modalityName = selectedModality.label?.toLowerCase() || '';

    return subjects.filter((subject) => {
      const subjectModality = subject.modality?.name?.toLowerCase() || '';

      // Online program: only online and online-autoatendidas subjects
      if (modalityName.includes('online') && !modalityName.includes('semi')) {
        if (modalityName.includes('autoatendida')) {
          // Online-Autoatendida: only online-autoatendida subjects
          return subjectModality.includes('autoatendida');
        }
        // Online: online and online-autoatendidas
        return subjectModality.includes('online');
      }

      // Presencial program: only presencial subjects
      if (
        modalityName.includes('presencial') &&
        !modalityName.includes('semi')
      ) {
        return (
          subjectModality.includes('presencial') &&
          !subjectModality.includes('semi')
        );
      }

      // Semipresencial program: online, online-autoatendida, semipresencial, presencial
      if (modalityName.includes('semi')) {
        return (
          subjectModality.includes('online') ||
          subjectModality.includes('presencial') ||
          subjectModality.includes('semi')
        );
      }

      return true;
    });
  }, [selectedModality, subjects]);

  // Validate units before submit
  const validateUnits = useCallback(() => {
    let isValid = true;
    let totalSubjects = 0;

    if (!units || units.length === 0) {
      setError('units', {
        type: 'manual',
        message: t(`${prefix}.validation.atLeastOneUnit`),
      });
      return false;
    }

    units.forEach((unit, index) => {
      if (!unit.name || unit.name.trim() === '') {
        setError(`units.${index}.name` as any, {
          type: 'manual',
          message: msgValidations.required,
        });
        isValid = false;
      }

      if (!unit.subjects || unit.subjects.length === 0) {
        setError(`units.${index}.subjects` as any, {
          type: 'manual',
          message: t(`${prefix}.validation.atLeastOneSubjectPerUnit`),
        });
        isValid = false;
      } else {
        totalSubjects += unit.subjects.length;
      }
    });

    if (totalSubjects < 2) {
      setError('units', {
        type: 'manual',
        message: t(`${prefix}.validation.atLeastTwoSubjects`),
      });
      isValid = false;
    }

    return isValid;
  }, [units, setError, t, msgValidations.required]);

  const onSubmit = async (formValues: ProgramFormValues) => {
    clearErrors();

    if (!validateUnits()) {
      return;
    }

    await onSubmitProp(formValues);
  };

  return (
    <Fragment>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col xs={12}>
              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.title`)} />
                  <p>{t(`${prefix}.subtitle`)}</p>

                  <Row>
                    <Col xs={12} md={6} className="pt-2">
                      <TextInput
                        name="name"
                        label={t(`${prefix}.name`)}
                        control={control}
                        rules={{
                          required: msgValidations.required,
                          maxLength: {
                            value: 255,
                            message: t('common.validations.maxLength', {
                              length: 255,
                            }),
                          },
                        }}
                      />
                    </Col>
                    <Col xs={12} md={6} className="pt-2">
                      <TextInput
                        name="code"
                        label={t(`${prefix}.code`)}
                        control={control}
                        rules={{
                          required: msgValidations.required,
                          maxLength: {
                            value: 50,
                            message: t('common.validations.maxLength', {
                              length: 50,
                            }),
                          },
                        }}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={6} className="pt-2">
                      <Select
                        name="modality"
                        label={t(`${prefix}.modality`)}
                        options={modalities}
                        control={control}
                        rules={{ required: msgValidations.required }}
                      />
                    </Col>
                    <Col xs={12} md={6} className="pt-2">
                      <Select
                        name="school"
                        label={t(`${prefix}.school`)}
                        options={schools}
                        control={control}
                        rules={{ required: msgValidations.required }}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={6} className="pt-2">
                      <TextInput
                        name="totalHours"
                        label={t(`${prefix}.totalHours`)}
                        control={control}
                        disabled
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              {activityTypes.length > 0 && (
                <Row className="mb-4">
                  <Col xs={12}>
                    <SectionTitle text={t(`${prefix}.activityHoursTitle`)} />
                  </Col>
                  {activityTypes.map((activityType) => (
                    <Col xs={12} md={6} key={activityType.id} className="pt-2">
                      <TextInput
                        name={`activityHours.${activityType.id}` as any}
                        control={control}
                        label={activityType.name}
                        rules={{
                          required: msgValidations.required,
                          pattern: rulesPatternDecimal,
                        }}
                        formatter={formatToOnlyPositiveIntegers}
                      />
                    </Col>
                  ))}
                </Row>
              )}

              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.description`)} />
                </Col>
                <Col xs={12} className="pt-2">
                  <TextAreaInput
                    name="description"
                    label={''}
                    control={control}
                    maxLength={4000}
                  />
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.curriculumTitle`)} />
                </Col>
                <Col xs={12}>
                  <ProgramCurriculum
                    control={control}
                    subjects={filteredSubjects}
                    selectedModality={selectedModality}
                  />
                  {Object.keys(errors).length > 0 && (
                    <p className="text-danger small mt-2">
                      {(errors.units as any)?.[1]?.subjects?.message}
                      {(errors.units as any)?.message}
                    </p>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={6}>
                  <SectionTitle text={t(`${prefix}.curriculumImage.title`)} />
                  <span className="fs-16">
                    {t(`${prefix}.curriculumImage.description`)}
                  </span>
                </Col>
                <Col xs={12} md={6}>
                  <AzureFilesUploaderControlled
                    name="curriculumImage"
                    control={control}
                    accept={['images']}
                    label={t(`${prefix}.curriculumImage.inputTitle`)}
                    btnText={t(`${prefix}.curriculumImage.inputBtn`)}
                  />
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.certificate`)} />
                </Col>
                <Col xs={12} md={6} className="pt-2">
                  <CheckInput
                    name="hasCertificate"
                    label={t(`${prefix}.hasCertificate`)}
                    control={control}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          {children({ isSubmitting })}
        </Form>
      </FormProvider>
    </Fragment>
  );
};

export default ProgramForm;
